import {
  Box,
  List,
  ListItem,
  ListItemText,
  Typography,
  CircularProgress,
} from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import DoodleSpinner from './DoodleSpinner'
import { COLORS } from '../theme'

export interface ProgressItem {
  name: string
  status: 'pending' | 'processing' | 'done' | 'error'
  error?: string
}

interface UploadProgressScreenProps {
  title: string
  subtitle?: string
  items: ProgressItem[]
  overallProgress: number
}

export default function UploadProgressScreen({
  title,
  subtitle,
  items,
  overallProgress,
}: UploadProgressScreenProps) {
  const doneCount = items.filter(i => i.status === 'done').length

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8, px: 4 }}>
      {/* Doodle + percentage */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, mb: 2 }}>
        <DoodleSpinner />
        <Typography
          variant="body2"
          sx={{
            color: COLORS.primary,
            fontWeight: 600,
            fontSize: '0.9rem',
            opacity: 0.7,
          }}
        >
          {Math.round(overallProgress)}%
        </Typography>
      </Box>

      <Typography variant="h6" sx={{ color: COLORS.primary, fontWeight: 500, mb: 0.5 }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="body2" sx={{ color: COLORS.secondary, mb: 1 }}>
          {subtitle}
        </Typography>
      )}
      <Typography
        variant="caption"
        role="progressbar"
        aria-valuenow={doneCount}
        aria-valuemin={0}
        aria-valuemax={items.length}
        aria-label="Overall progress"
        sx={{ color: COLORS.secondary, mb: 2 }}
      >
        {doneCount} of {items.length} complete
      </Typography>

      {/* Per-item status list */}
      <List sx={{ width: '100%', maxWidth: 480, flex: 1, overflow: 'auto' }} disablePadding>
        {items.map(item => (
          <ListItem key={item.name} sx={{ borderBottom: `1px solid ${COLORS.border}`, py: 1 }}>
            <Box
              sx={{ mr: 1.5, display: 'flex', alignItems: 'center' }}
              aria-label={
                item.status === 'done' ? 'Complete' :
                item.status === 'processing' ? 'Processing' :
                item.status === 'error' ? 'Error' :
                'Waiting'
              }
            >
              <StatusIcon status={item.status} />
            </Box>
            <ListItemText
              primary={item.name}
              secondary={
                item.status === 'error'
                  ? 'Could not be processed'
                  : item.status === 'processing'
                    ? 'Processing...'
                    : item.status === 'done'
                      ? 'Complete'
                      : 'Waiting...'
              }
              primaryTypographyProps={{
                variant: 'body2',
                sx: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
              }}
              secondaryTypographyProps={{
                sx: { color: item.status === 'error' ? COLORS.error : COLORS.muted },
              }}
            />
          </ListItem>
        ))}
      </List>

    </Box>
  )
}

function StatusIcon({ status }: { status: ProgressItem['status'] }) {
  switch (status) {
    case 'done':
      return <CheckCircleOutlineIcon aria-hidden="true" sx={{ color: 'green', fontSize: 20 }} />
    case 'processing':
      return <CircularProgress aria-hidden="true" size={18} sx={{ color: COLORS.primary }} />
    case 'error':
      return <ErrorOutlineIcon aria-hidden="true" sx={{ color: 'red', fontSize: 20 }} />
    default:
      return (
        <Box
          aria-hidden="true"
          sx={{
            width: 18,
            height: 18,
            borderRadius: '50%',
            border: `2px solid ${COLORS.lightGray}`,
          }}
        />
      )
  }
}
